import { getApiBase } from "../config";
import { clearAuth, getToken } from "../auth";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * CreateOrganizationPage.tsx (Production-ready)
 * - Calls SUPERADMIN-only: POST /api/register
 * - Sends JSON body matching schemas.OrgCreate:
 *   {
 *     company_name, company_address, city, state, zip,
 *     owner_name, owner_email, owner_phone, owner_password
 *   }
 *
 * Assumptions:
 * - JWT is stored in localStorage under "access_token"
 * - If API returns 401 -> logout + redirect to "/"
 * - You have route: /admin/organizations/new
 */

const API_BASE = getApiBase();
const ENDPOINT = "/admin/register";

type ApiErr = { detail?: any };

function logoutAndRedirect(navigate: ReturnType<typeof useNavigate>) {
  clearAuth();
  navigate("/", { replace: true });
}

async function readError(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as ApiErr;
    if (typeof data?.detail === "string") return data.detail;
    if (data?.detail) return JSON.stringify(data.detail);
  } catch {}
  return `Request failed (${res.status})`;
}

function isValidEmail(email: string): boolean {
  // simple + effective
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function normalizePhone(phone: string): string {
  // keep + and digits, strip other stuff
  return phone.replace(/[^\d+]/g, "").trim();
}

export default function CreateOrganizationPage() {
  const navigate = useNavigate();

  // Company
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zip, setZip] = useState("");

  // Owner
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // UX
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const phoneNormalized = useMemo(
    () => normalizePhone(ownerPhone),
    [ownerPhone],
  );

  const validation = useMemo(() => {
    const errs: string[] = [];

    if (!companyName.trim()) errs.push("Company name is required.");
    if (!companyAddress.trim()) errs.push("Company address is required.");
    if (!city.trim()) errs.push("City is required.");
    if (!stateCode.trim()) errs.push("State is required.");
    if (!zip.trim()) errs.push("ZIP is required.");

    if (!ownerName.trim()) errs.push("Owner name is required.");
    if (!ownerEmail.trim()) errs.push("Owner email is required.");
    if (ownerEmail.trim() && !isValidEmail(ownerEmail.trim()))
      errs.push("Owner email looks invalid.");

    if (!phoneNormalized) errs.push("Owner phone is required.");
    // keep it permissive: 8-16 digits is typical globally
    const digitsOnly = phoneNormalized.replace(/[^\d]/g, "");
    if (phoneNormalized && (digitsOnly.length < 8 || digitsOnly.length > 16)) {
      errs.push("Owner phone should be 8–16 digits (country code allowed).");
    }

    if (!ownerPassword) errs.push("Owner password is required.");
    if (ownerPassword && ownerPassword.length < 8)
      errs.push("Owner password must be at least 8 characters.");
    if (ownerPassword !== confirmPassword) errs.push("Passwords do not match.");

    return {
      ok: errs.length === 0,
      errs,
    };
  }, [
    companyName,
    companyAddress,
    city,
    stateCode,
    zip,
    ownerName,
    ownerEmail,
    phoneNormalized,
    ownerPassword,
    confirmPassword,
  ]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setOk(null);

    if (!validation.ok) {
      setError(validation.errs[0] || "Please fix the form errors.");
      return;
    }

    const token = getToken();
    if (!token) {
      setError("Missing session token. Please login again.");
      logoutAndRedirect(navigate);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        company_name: companyName.trim(),
        company_address: companyAddress.trim(),
        city: city.trim(),
        state: stateCode.trim().toUpperCase(),
        zip: zip.trim(),

        owner_name: ownerName.trim(),
        owner_email: ownerEmail.trim().toLowerCase(),
        owner_phone: phoneNormalized,
        owner_password: ownerPassword,
      };

      const res = await fetch(`${API_BASE}${ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        if (res.status === 401) {
          logoutAndRedirect(navigate);
          return;
        }
        const msg = await readError(res);
        if (res.status === 403) {
          setError("Not authorized (SUPERADMIN only).");
          return;
        }
        setError(msg);
        return;
      }

      const data = await res.json().catch(() => ({}) as any);
      setOk("Organization created.");

      // If backend returns org object with id, you can redirect to detail page
      // e.g. /admin/organizations/:id
      const orgId = data?.id;
      setTimeout(() => {
        if (orgId) navigate(`/admin/organizations/${orgId}`, { replace: true });
        else navigate("/admin", { replace: true });
      }, 500);
    } catch (err: any) {
      setError(err?.message || "Failed to create organization.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 py-10">
      <div className="mx-auto max-w-3xl p-6">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold text-slate-500">Admin</div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              Create Organization
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Creates a tenant + the first OWNER user (SUPERADMIN only).
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-300 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-900 dark:text-slate-100 hover:bg-slate-100"
          >
            Back
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {ok && (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {ok}
          </div>
        )}
        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Company Card */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Company Details
              </div>
              <div className="text-xs text-slate-500">Required</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Company Name
                </label>
                <input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="e.g., Crown Law Firm"
                  autoComplete="organization"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Company Address
                </label>
                <input
                  value={companyAddress}
                  onChange={(e) => setCompanyAddress(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="Street / Suite"
                  autoComplete="street-address"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  City
                </label>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="City"
                  autoComplete="address-level2"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  State
                </label>
                <input
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="e.g., IA"
                  autoComplete="address-level1"
                />
                <div className="mt-1 text-xs text-slate-500">
                  Use postal code (IA/IL/CA) or a state name if you prefer—your
                  backend will store as text.
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  ZIP
                </label>
                <input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="ZIP / Postal"
                  autoComplete="postal-code"
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>

          {/* Owner Card */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Owner User
              </div>
              <div className="text-xs text-slate-500">Role: OWNER</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Owner Name
                </label>
                <input
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="Full name"
                  autoComplete="name"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Owner Email
                </label>
                <input
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="owner@lawfirm.com"
                  autoComplete="email"
                  inputMode="email"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Owner Phone
                </label>
                <input
                  value={ownerPhone}
                  onChange={(e) => setOwnerPhone(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="+1 555 123 4567"
                  autoComplete="tel"
                  inputMode="tel"
                />
                <div className="mt-1 text-xs text-slate-500">
                  Stored as text. Keep country code if applicable.
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Owner Password
                </label>
                <input
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="Minimum 8 characters"
                  type="password"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Confirm Password
                </label>
                <input
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-slate-400/10"
                  placeholder="Re-type password"
                  type="password"
                  autoComplete="new-password"
                />
                {ownerPassword &&
                  confirmPassword &&
                  ownerPassword !== confirmPassword && (
                    <div className="mt-1 text-xs text-red-600">
                      Passwords do not match.
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col md:flex-row gap-3">
            <button
              type="submit"
              disabled={submitting || !validation.ok}
              className="flex-1 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Creating..." : "Create Organization"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/admin", { replace: true })}
              className="flex-1 rounded-xl border border-slate-300 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-900 dark:text-slate-100 hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>

          {!validation.ok && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-sm text-slate-700 dark:text-slate-300">
              <div className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Fix these:
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {validation.errs.slice(0, 6).map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
